import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import type { ApiSuccessRatePerTopicStat } from "../types";

interface SuccessRatePerTopicChartProps {
  data: ApiSuccessRatePerTopicStat[];
  isLoading: boolean;
}

const SuccessRatePerTopicChart = ({
  data,
  isLoading,
}: SuccessRatePerTopicChartProps) => {
  const { t } = useTranslation();

  const chartConfig = {
    success_rate: {
      label: t("modules.stats.part2.success_rate_per_topic.chart_label"),
      color: "var(--chart-1)",
    },
  } satisfies ChartConfig;

  // `success_rate` is a ratio in [0, 1]; the chart axis is a percentage in
  // [0, 100]. Missing values (too few respondents) render as 0.
  const chartData = data.map((d) => ({
    topic: d.topic_name,
    success_rate:
      d.success_rate === null ? 0 : Math.round(d.success_rate * 100),
  }));

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <ChartContainer config={chartConfig} className="max-h-72 w-full">
      <BarChart accessibilityLayer data={chartData}>
        <CartesianGrid vertical={false} />
        <YAxis
          domain={[0, 100]}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value: number) => `${value}%`}
        />
        <XAxis
          dataKey="topic"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          interval={0}
          tickFormatter={(value: string) =>
            value.length > 10 ? `${value.slice(0, 10)}…` : value
          }
        />
        <ChartTooltip
          cursor={false}
          content={<ChartTooltipContent formatter={(value) => `${value}%`} />}
        />
        <Bar
          dataKey="success_rate"
          fill="var(--color-success_rate)"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ChartContainer>
  );
};

export default SuccessRatePerTopicChart;
